import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { calcularFechamentoExterior, linhaFechamentoExteriorConcluido } from './fechamentoExteriorPolicy';
import { linhaSemMoedaPropria } from './moedaFornecedorPolicy';
import { totalBrutoLinha } from './valorBrlPolicy';

// Aviso ao Salvar (Rafael 02/10): lista as linhas em branco ou zeradas de cada coluna para ninguém
// esquecer um item. Só AVISA — nunca preenche nada e nunca bloqueia o salvar.
// - Pré-evento: toda linha com total em branco/zero.
// - Negociado / Fechamento: só se a coluna foi "iniciada" (alguma linha com valor ≠ 0); então lista as
//   linhas em branco/zeradas. Fechamento exterior concluído (ledger) conta como preenchido.
// - Linha em branco numa coluna, mas com valor numa coluna seguinte (incluída depois), não entra.
// - Fora: linhas automáticas (FEE %, FEE AGÊNCIA 130, 1945/imposto/NF Access, IOF/remessa/NF
//   calculados) e linhas de rascunho sem produto.

export const COLUNAS_ITENS_EM_BRANCO = ['PreEvento', 'Negociado', 'Fechamento'];
export const ROTULO_COLUNA_ITENS_EM_BRANCO = { PreEvento: 'Pré-evento', Negociado: 'Negociado', Fechamento: 'Fechamento (valor real)' };

const numero = (valor) => {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    if (valor === null || valor === undefined) return 0;
    const texto = String(valor).trim();
    if (!texto) return 0;
    const convertido = parseFloat(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : 0;
};

const semProduto = (linha = {}) => {
    const produto = linha?.produto;
    if (!produto) return true;
    const id = typeof produto === 'object' ? produto.id : produto;
    const nome = typeof produto === 'object' ? produto.nome : null;
    return (id === null || id === undefined || String(id).trim() === '') && !String(nome ?? '').trim();
};

// Linha que entra no aviso: tem produto e não é automática.
export function linhaEntraNoAvisoEmBranco(linha = {}) {
    if (!linha || semProduto(linha)) return false;
    return !linhaSemMoedaPropria(linha) && !linha.impostoNfAccessManualAgencia;
}

// Valor da linha na coluna (o total gravado na grade; sem ele, a fórmula da linha).
const valorNaColuna = (linha = {}, coluna) => {
    const campo = `total${coluna}`;
    const informado = linha[campo] !== undefined && linha[campo] !== null && String(linha[campo]).trim() !== '';
    return informado ? numero(linha[campo]) : totalBrutoLinha(linha, coluna);
};
const fechamentoExteriorConcluido = (linha, fornecedorAccess) => {
    try { return calcularFechamentoExterior(linha, fornecedorAccess).completo === true; } catch (erro) { return false; }
};
const preenchida = (linha, coluna, fornecedorAccess) => {
    if (coluna === 'Fechamento' && (linhaFechamentoExteriorConcluido(linha) || fechamentoExteriorConcluido(linha, fornecedorAccess))) return true;
    return Math.abs(valorNaColuna(linha, coluna)) >= 0.005;
};

// { PreEvento: [...], Negociado: [...], Fechamento: [...], iniciadas: { Negociado, Fechamento }, total }
// Cada item: { grupoIndex, linhaIndex, numero, grupo, produto, rotulo }.
export function linhasEmBrancoPorColuna(orcamento = {}, { fornecedorAccess = null } = {}) {
    const candidatas = [];
    (orcamento?.orcamentoitem || []).forEach((grupo, grupoIndex) => (grupo?.subitem || []).forEach((linha, linhaIndex) => {
        if (linhaEntraNoAvisoEmBranco(linha)) candidatas.push({ grupo, grupoIndex, linha, linhaIndex });
    }));
    const iniciadas = {
        PreEvento: true,
        Negociado: candidatas.some(({ linha }) => preenchida(linha, 'Negociado', fornecedorAccess)),
        Fechamento: candidatas.some(({ linha }) => preenchida(linha, 'Fechamento', fornecedorAccess)),
    };
    const resultado = { PreEvento: [], Negociado: [], Fechamento: [], iniciadas: { Negociado: iniciadas.Negociado, Fechamento: iniciadas.Fechamento }, total: 0 };
    COLUNAS_ITENS_EM_BRANCO.forEach((coluna) => {
        if (!iniciadas[coluna]) return;
        const seguintes = COLUNAS_ITENS_EM_BRANCO.slice(COLUNAS_ITENS_EM_BRANCO.indexOf(coluna) + 1);
        candidatas.forEach(({ grupo, grupoIndex, linha, linhaIndex }) => {
            if (preenchida(linha, coluna, fornecedorAccess)) return;
            // Linha incluída depois (valor só numa coluna seguinte) não foi esquecida: não avisa.
            if (seguintes.some((proxima) => preenchida(linha, proxima, fornecedorAccess))) return;
            const nomeGrupo = String(AgenciaOrcamentoPolicy.nomeGrupoExibicao(grupo?.produto || {}) || grupo?.nome || '').trim() || 'Grupo';
            const nomeProduto = String(AgenciaOrcamentoPolicy.nomeSubitemExibicao(grupo, linha.produto || {}) || linha.produto?.nome || '').trim() || 'Serviço';
            resultado[coluna].push({
                grupoIndex, linhaIndex, numero: linhaIndex + 1, grupo: nomeGrupo, produto: nomeProduto,
                rotulo: `${nomeGrupo} · linha ${linhaIndex + 1} — ${nomeProduto}`,
            });
        });
    });
    resultado.total = resultado.PreEvento.length + resultado.Negociado.length + resultado.Fechamento.length;
    return resultado;
}

// Lista curta para o modal: até `limite` itens e quantos ficaram de fora ("e mais N").
export function resumirItensEmBranco(itens = [], limite = 10) {
    return { visiveis: itens.slice(0, limite), restantes: Math.max(0, itens.length - limite) };
}
